import { DashboardSummary } from '@/types/dashboard';
import { FilterOptions, SelectedFilters } from '@/types/filters';
import axiosInstance from '../utils/axios';

/**
 * Performance API Caching Mechanism
 * ----------------------------------
 * This file implements a caching mechanism to improve performance for frequently called APIs.
 * 
 * Key features:
 * 1. Cached data is stored with a timestamp and the params string used for the request
 * 2. Cache validity is checked using the isValidCache function which compares:
 *    - If the cache exists for the endpoint
 *    - If the cache timestamp is recent (within CACHE_DURATION)
 *    - If the params match the current request
 * 3. Cache is updated using the updateCache function
 * 4. All caches can be cleared using clearPerformanceCache
 * 
 * Performance benefits:
 * - Prevents duplicate API calls for the same data within a short time period
 * - Significantly reduces network traffic and server load
 * - Improves UI responsiveness by serving cached data instantly
 * 
 * To manually clear caches:
 * - Use clearFilterOptionsCache() for filter options
 * - Use clearPerformanceCache() for all performance data
 */

const API_BASE_URL = 'https://neokartuatbak.cholainsurance.com/api';
// const API_BASE_URL = 'http://localhost:8000/api';
// Use the global axiosInstance instead of creating a new one
const api = axiosInstance;

// Set the base URL for API calls
api.defaults.baseURL = API_BASE_URL;

// Cache for filter options to prevent multiple API calls
let cachedFilterOptions: FilterOptions | null = null;
let cacheTimestamp: number | null = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

// Cache for performance APIs
interface PerformanceAPICache {
  data: any;
  timestamp: number;
  params: string; // Store URL params to match requests with same filters
}

const performanceCache: Record<string, PerformanceAPICache | null> = {
  'overall': null,
  'lob': null,
  'regionMap': null,
  'productChannel': null,
  'channelProduct': null,
  'productStatus': null,
  'channelStatus': null,
  'subchannelStatus': null,
  'productConversion': null,
  'channelConversion': null,
  'subchannelConversion': null,
  'channelBreakdown': null,
  'managerBreakdown': null,
};

// Helper function to check if cache is valid for a specific endpoint and params
const isValidCache = (endpoint: keyof typeof performanceCache, params: string): boolean => {
  const cache = performanceCache[endpoint];
  if (!cache) return false;
  
  const now = Date.now();
  return !!cache.timestamp && (now - cache.timestamp < CACHE_DURATION) && cache.params === params;
};

// Helper function to update cache for a specific endpoint
const updateCache = (endpoint: keyof typeof performanceCache, data: any, params: string): void => {
  performanceCache[endpoint] = {
    data,
    timestamp: Date.now(),
    params,
  };
};

// Function to clear all performance caches
export const clearPerformanceCache = (): void => {
  Object.keys(performanceCache).forEach(key => {
    performanceCache[key as keyof typeof performanceCache] = null;
  });
};

// No need to add request interceptor here as it's already in the axiosInstance

export interface DrillDownPolicy {
  id: number;
  policyNumber: string;
  product: string;
  channel: string;
  status: string;
  manager: {
    rm: string;
    zm: string;
    nsm: string;
  };
  agent: {
    code: string;
    name: string;
  };
  updateDate: string;
}

export interface BusinessHeadSummary {
  business_head: string;
  date_range: {
    start: string | null;
    end: string | null;
  };
  metrics: {
    [key: string]: {
      ftm: {
        total_base: number;
        conversion: number;
        conv_rate: number;
      };
      aod: {
        total_base: number;
        conversion: number;
        conv_rate: number;
      };
      rnyear_metrics: {
        [key: string]: {
          ftm: {
            total_base: number;
            conversion: number;
            conv_rate: number;
          };
          aod: {
            total_base: number;
            conversion: number;
            conv_rate: number;
          };
        };
      };
    };
  };
}

export interface Metrics {
  ftm: {
    total_base: number;
    conversion: number;
    conv_rate: number;
  };
  aod: {
    total_base: number;
    conversion: number;
    conv_rate: number;
  };
}

export interface RegionHierarchyFilters {
  startDate?: string;
  endDate?: string;
  level?: string;
  parent_id?: string;
  hierarchy_path?: string;
  selected_months?: number[];
  selected_years?: number[];
  selected_lob?: string[];
  selected_products?: (string | ProductInfo)[];
  selected_sub_products?: (string | ProductInfo)[];
  selected_channels?: string[];
  selected_sub_channels?: string[];
}

export interface RegionManagerResponse {
  managers: ManagerData[];
  total_metrics: {
    ftm: {
      total_base: number;
      conversion: number;
      conv_rate: number;
    };
    aod: {
      total_base: number;
      conversion: number;
      conv_rate: number;
    };
  };
}

export interface ManagerData {
  id: string;
  name: string;
  role: string;
  channel: string | null;
  state: string | null;
  metrics: Metrics;
  hierarchy_path?: string;
}

export interface AgencyDetails {
  channels: {
    [key: string]: {
      name: string;
      metrics: Metrics;
      subchannels: {
        [key: string]: {
          name: string;
          metrics: Metrics;
          agencies: {
            [key: string]: {
              name: string;
              imd_code: string;
              sol_id: string;
              metrics: Metrics;
            };
          };
        };
      };
    };
  };
  hierarchy: {
    fls: { id: string; name: string; };
    sm: { id: string; name: string; };
    sh: { id: string; name: string; };
    am: { id: string; name: string; };
    rm: { id: string; name: string; };
    zm: { id: string; name: string; };
    nsm: { id: string; name: string; };
    vh: { id: string; name: string; };
    bh: { id: string; name: string; };
  };
  location: {
    partner_region: string;
    partner_zone: string;
    chola_branch: string;
    chola_cluster: string;
    chola_state: string;
  };
}

export interface ProductHierarchy {
  name: string;
  mis_product?: string;
  metrics: {
    ftm: {
      total_base: number;
      conversion: number;
      conv_rate: number;
    };
    aod: {
      total_base: number;
      conversion: number;
      conv_rate: number;
    };
  };
  products?: {
    [key: string]: {
      name: string;
      mis_product: string;
      metrics: {
        ftm: {
          total_base: number;
          conversion: number;
          conv_rate: number;
        };
        aod: {
          total_base: number;
          conversion: number;
          conv_rate: number;
        };
      };
      rnyears: {
        [key: string]: {
          name: string;
          metrics: {
            ftm: {
              total_base: number;
              conversion: number;
              conv_rate: number;
            };
            aod: {
              total_base: number;
              conversion: number;
              conv_rate: number;
            };
          };
        };
      };
    };
  };
}

export interface ProductStatusMatrix {
  products: string[];
  metrics: {
    [key: string]: {
      status_leads: number;
      status_converted: number;
      total_leads: number;
      total_converted: number;
      status_percentage: number;
      status_conversion_rate: number;
      total_conversion_rate: number;
    };
  };
  selected_status: string;
}

export interface ChannelStatusMatrix {
  channels: string[];
  metrics: {
    [key: string]: {
      status_leads: number;
      status_converted: number;
      total_leads: number;
      total_converted: number;
      status_percentage: number;
      status_conversion_rate: number;
      total_conversion_rate: number;
    };
  };
  selected_status: string;
}

export interface SubChannelStatusMatrix {
  subChannels: string[];
  metrics: {
    [key: string]: {
      status_leads: number;
      status_converted: number;
      total_leads: number;
      total_converted: number;
      status_percentage: number;
      status_conversion_rate: number;
      total_conversion_rate: number;
    };
  };
  selected_status: string;
  parent_channel: string;
}

export interface FilterMapping {
  lob_to_product: Record<string, string[]>;
  product_to_channel: Record<string, string[]>;
  channel_to_subchannel: Record<string, string[]>;
}

export interface ProductInfo {
  product_name: string;
  mis_product: string;
  display_name: string;
}

export interface DashboardFilterOptions {
  channel: string[];
  lob: string[];
  mis_rnyears: string[];
  months: string[];
  product_group: string[];
  sub_product: string[];
  subchannel: string[];
  years: string[];
}

export const fetchFilterOptions = async (): Promise<FilterOptions> => {
  // Check if we have valid cached data
  const now = Date.now();
  if (cachedFilterOptions && cacheTimestamp && now - cacheTimestamp < CACHE_DURATION) {
    return cachedFilterOptions;
  }

  try {
    const response = await api.get('/dashboard/filters');
    
    // Cache the response
    cachedFilterOptions = {
      months: response.data.months || [],
      years: response.data.years || [],
      lob: response.data.lob || [],
      product_group: response.data.product_group || [],
      channel: response.data.channel || [],
      subchannel: response.data.subchannel || [],
      sub_product: response.data.sub_product || [],
    };
    cacheTimestamp = now;
    
    return cachedFilterOptions;
  } catch (error) {
    console.error('Error fetching filter options:', error);
    throw error;
  }
};

// Function to manually clear the cache if needed
export const clearFilterOptionsCache = () => {
  cachedFilterOptions = null;
  cacheTimestamp = null;
};

/**
 * Clears all caches after data uploads that modify the underlying data
 * This should be called after uploads that could affect the dashboard data:
 * - Lead data uploads
 * - Hierarchy data uploads
 * - Product uploads
 * - Renewal target uploads
 * - Lead status updates
 */
export const clearCachesAfterUpload = () => {
  // Clear filter options cache
  clearFilterOptionsCache();
  
  // Clear performance caches
  clearPerformanceCache();
  
};

// =============================
// Renewal Notice Data - Uploads
// =============================

export interface RenewalNoticeUploadStartResponse {
  task_id: string;
  message?: string;
}

export type RenewalNoticeTaskStatus = 'pending' | 'processing' | 'completed' | 'failed' | 'warning';

export interface RenewalNoticeUploadStatusResponse {
  task_id: string;
  status: RenewalNoticeTaskStatus;
  progress?: number;
  total_rows?: number;
  processed_rows?: number;
  successful_rows?: number;
  failed_rows?: number;
  errors?: Array<{ row: number; message: string }> | string[];
  message?: string;
}

export const uploadRenewalNoticeData = async (
  file: File,
  truncate: boolean = false
): Promise<RenewalNoticeUploadStartResponse> => {
  const formData = new FormData();
  formData.append('file', file);

  const params = new URLSearchParams();
  params.append('truncate', String(truncate));

  const url = `/uploads/renewal-notice-data${params.toString() ? `?${params.toString()}` : ''}`;
  try {
    const response = await api.post(url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });

    // Clear dashboard caches so new data reflects
    clearCachesAfterUpload();
    return response.data as RenewalNoticeUploadStartResponse;
  } catch (error: any) {
    const detail = error?.response?.data?.detail || 'Failed to start renewal notice upload';
    throw new Error(detail);
  }
};

export const getRenewalNoticeUploadStatus = async (
  taskId: string
): Promise<RenewalNoticeUploadStatusResponse> => {
  try {
    const response = await api.get(`/uploads/renewal-notice-data/status/${encodeURIComponent(taskId)}`);
    return response.data as RenewalNoticeUploadStatusResponse;
  } catch (error: any) {
    const detail = error?.response?.data?.detail || 'Failed to fetch upload status';
    throw new Error(detail);
  }
};

export const downloadRenewalNoticeTemplate = async (): Promise<void> => {
  try {
    const response = await api.get('/uploads/renewal-notice-data/template', {
      responseType: 'blob'
    });
    
    // Create a download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'Renewal_Data_Upload_Template.xlsx');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error: any) {
    const detail = error?.response?.data?.detail || 'Failed to download template';
    throw new Error(detail);
  }
};

/**
 * Download error log for renewal notice data upload
 * Downloads CSV file with error details including row data
 */
export const downloadRenewalNoticeErrorLog = async (taskId: string): Promise<void> => {
  try {
    const response = await api.get(`/uploads/renewal-notice-data/errors/${taskId}`, {
      responseType: 'blob',
      timeout: 30000  // 30 seconds timeout to prevent infinite waiting
    });
    
    // Create a download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    
    // Extract filename from content-disposition header or use default
    const contentDisposition = response.headers['content-disposition'];
    let filename = `renewal_notice_errors_${taskId}_${new Date().getTime()}.csv`;
    
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1];
      }
    }
    
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error: any) {
    const detail = error?.response?.data?.detail || 'Failed to download error log';
    throw new Error(detail);
  }
};

// Helper function to convert SelectedFilters to RegionHierarchyFilters format
export const convertToRegionHierarchyFilters = (filters: SelectedFilters): RegionHierarchyFilters => {
  return {
    selected_months: filters.selectedMonth ? [parseInt(filters.selectedMonth, 10)] : undefined,
    selected_years: filters.selectedYear ? [parseInt(filters.selectedYear, 10)] : undefined,
    selected_lob: filters.selectedLOB.length > 0 ? filters.selectedLOB : undefined,
    selected_products: filters.selectedProducts.length > 0 ? filters.selectedProducts : undefined,
    selected_sub_products: filters.selectedSubProducts.length > 0 ? filters.selectedSubProducts : undefined,
    selected_channels: filters.selectedChannels.length > 0 ? filters.selectedChannels : undefined,
    selected_sub_channels: filters.selectedSubChannels.length > 0 ? filters.selectedSubChannels : undefined,
  };
};

export const fetchDashboardSummary = async (filters?: SelectedFilters, region?: string): Promise<DashboardSummary> => {
  // Only include filters that have been selected
  const payload: Record<string, any> = {};

  if (filters?.selectedMonth) {
    payload.selected_months = [filters.selectedMonth];
  }
  
  if (filters?.selectedYear) {
    // Find the year option to determine which calendar year to use
    // Use cached options first without triggering a new API call
    const options = cachedFilterOptions || await fetchFilterOptions();
    const yearOption = options.years.find(y => y.id === filters.selectedYear);
    
    if (yearOption && filters.selectedMonth) {
      const selectedMonth = parseInt(filters.selectedMonth);
      // Months 1-3 (Jan-Mar) use end_year, months 4-12 (Apr-Dec) use start_year
      const calendarYear = selectedMonth >= 1 && selectedMonth <= 3 
        ? yearOption.end_year.toString()
        : yearOption.start_year.toString();
      
      payload.selected_years = [calendarYear];
    } else {
      // If month is not selected or year option not found, use financial year ID
      payload.selected_years = [filters.selectedYear];
    }
  }
  
  if (filters?.selectedLOB?.length) {
    payload.selected_lob = filters.selectedLOB;
  }
  
  if (filters?.selectedProducts?.length) {
    const isProductInfo = (p: any): p is ProductInfo => p && typeof p === 'object' && 'mis_product' in p;
    // Convert ProductInfo objects to their mis_product values
    payload.selected_products = (filters.selectedProducts as any[]).map(p => isProductInfo(p) ? p.mis_product : p);
  }
  
  if (filters?.selectedChannels?.length) {
    payload.selected_channels = filters.selectedChannels;
  }

  if (filters?.selectedSubChannels?.length) {
    payload.selected_sub_channels = filters.selectedSubChannels;
  }

  if (region) {
    payload.selected_region = region;
  }

  // Request trend data comparison with previous period
  payload.include_trends = true;

  const response = await api.post('/dashboard/summary', payload);
  
  // If the backend doesn't yet support trend data, we can add mock trend data here temporarily
  // This should be removed once the backend properly implements trend data
  if (!response.data.trends) {
    response.data.trends = {
      totalLeads: 12.5,
      convertedLeads: 8.2,
      conversionRate: -2.1
    };
  }
  
  // Log a warning if subChannelStatusBreakdown is missing from the backend response
  if (!response.data.subChannelStatusBreakdown) {
    console.warn('Backend API does not provide subChannelStatusBreakdown data. This feature requires backend implementation.');
  }
  
  return response.data;
};

// Propensity Predictions API
export interface PropensityFactor {
  feature: string;
  impact: number;
  impact_percentage: number;
  display_name: string;
  formatted_impact: string;
}

export interface PropensityPredictionItem {
  id: number;
  policy_id: string;
  customer_id: string;
  agent_code: string;
  verticle?: string;
  renewal_probability: string;
  predicted_renewal: number;
  top_positive_factors: PropensityFactor[];
  top_negative_factors: PropensityFactor[];
  created_at: string;
  updated_at: string;
  chola_state?: string;
  state_code?: string;
  channel?: string;
  sub_channel?: string;
  business_head?: string;
  vertical_head?: string;
  nsm_emp_name?: string;
  zm_emp_name?: string;
  rm_emp_name?: string;
  am_emp_name?: string;
  state_head?: string;
  sm_emp_name?: string;
  fls_emp_name?: string;
}

export interface GetPropensityPredictionsParams {
  limit?: number;
  offset?: number;
  search?: string; // by policy_id or customer_id
}

export interface PropensityPredictionsResponse {
  total: number;
  limit: number;
  offset: number;
  data: PropensityPredictionItem[];
}

export const getPropensityPredictions = async (
  params: GetPropensityPredictionsParams = {}
): Promise<PropensityPredictionsResponse> => {
  const query = new URLSearchParams();
  if (typeof params.limit === 'number') query.append('limit', String(params.limit));
  if (typeof params.offset === 'number') query.append('offset', String(params.offset));
  if (params.search) query.append('search', params.search);

  const url = `/propensity/propensity-predictions${query.toString() ? `?${query.toString()}` : ''}`;
  const response = await api.get(url);
  return response.data as PropensityPredictionsResponse;
};

// Propensity Manager Hierarchy Breakdown API
export interface PropensityHierarchyPolicyItem {
  policy_id: string;
  customer_id: string;
  agent_code: string;
  verticle?: string;
  renewal_probability: string;
  predicted_renewal: number;
  risk_level?: string;
  premium_amount?: number;
  channel?: string;
  sub_channel?: string;
}

export interface PropensityHierarchyNode {
  employee_id: string;
  employee_name: string;
  employee_role: string; // e.g., SM, FLS, etc.
  total_policies: number;
  total_predicted_renewals: number;
  predicted_renewal_rate: string; // percentage string like "60.0"
  average_renewal_probability: string; // e.g., "0.6136"
  high_risk_count: number;
  medium_risk_count: number;
  low_risk_count: number;
  subordinates?: Array<{
    employee_id: string;
    employee_name: string;
    role?: string; // sometimes present as lowercase (e.g., fls)
    employee_role: string;
    total_policies: number;
    total_predicted_renewals: number;
    predicted_renewal_rate: string;
    average_renewal_probability: string;
    high_risk_count: number;
    medium_risk_count: number;
    low_risk_count: number;
  }>;
  policies?: PropensityHierarchyPolicyItem[];
}

export interface GetPropensityHierarchyParams {
  target_employee_id?: string;
  target_role?: string; // backend expects e.g., fls, sm, etc.
  hierarchy_path?: string;
}

export const fetchPropensityHierarchyBreakdown = async (
  params: GetPropensityHierarchyParams = {}
): Promise<PropensityHierarchyNode> => {
  const query = new URLSearchParams();
  if (params.target_employee_id) query.append('target_employee_id', params.target_employee_id);
  if (params.target_role) query.append('target_role', params.target_role);
  if (params.hierarchy_path) query.append('hierarchy_path', params.hierarchy_path);

  const url = `/propensity/propensity-predictions/hierarchy-breakdown${query.toString() ? `?${query.toString()}` : ''}`;
  const response = await api.get(url);
  return response.data as PropensityHierarchyNode;
};

export const fetchRegionDrillDown = async (stateCode: string): Promise<DrillDownPolicy[]> => {
  try {
    const url = `/dashboard/drill-down/${encodeURIComponent(stateCode)}`;
    
    const response = await api.get(url);
    
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const fetchProductChannelMatrix = async (startDate?: string, endDate?: string, selectedRegion?: string) => {
  try {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (selectedRegion && selectedRegion !== 'all') params.append('selected_region', selectedRegion);

    const response = await api.get(`/dashboard/product-channel-matrix${params.toString() ? `?${params.toString()}` : ''}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching product-channel matrix:', error);
    throw error;
  }
};

export const fetchBusinessHeadSummary = async (
  businessHead: string,
  startDate?: string,
  endDate?: string,
  filters?: {
    selectedMonths?: number[];
    selectedYears?: number[];
    selectedLOB?: string[];
    selectedProducts?: (string | ProductInfo)[];
    selectedChannels?: string[];
    selectedSubChannels?: string[];
  }
): Promise<BusinessHeadSummary> => {
  try {
    const params = new URLSearchParams();
    params.append('business_head', businessHead);
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    
    // Add additional filters
    if (filters?.selectedMonths?.length) {
      filters.selectedMonths.forEach(month => params.append('selected_months', month.toString()));
    }
    if (filters?.selectedYears?.length) {
      filters.selectedYears.forEach(year => params.append('selected_years', year.toString()));
    }
    if (filters?.selectedLOB?.length) {
      filters.selectedLOB.forEach(lob => params.append('selected_lob', lob));
    }
    if (filters?.selectedProducts?.length) {
      filters.selectedProducts.forEach(product => {
        // If product is a ProductInfo object, use its mis_product property
        const productValue = typeof product === 'string' ? product : product.mis_product;
        params.append('selected_products', productValue);
      });
    }
    if (filters?.selectedChannels?.length) {
      filters.selectedChannels.forEach(channel => params.append('selected_channels', channel));
    }
    if (filters?.selectedSubChannels?.length) {
      filters.selectedSubChannels.forEach(subChannel => params.append('selected_sub_channels', subChannel));
    }

    const response = await api.get(`/dashboard/business-head-summary?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching business head summary:', error);
    throw error;
  }
};

export const fetchBusinessHeads = async (): Promise<string[]> => {
  try {
    const response = await api.get('/dashboard/business-heads');
    return response.data;
  } catch (error) {
    console.error('Error fetching business heads:', error);
    throw error;
  }
};

export const fetchRegionHierarchySummary = async (
  region: string,
  filters?: RegionHierarchyFilters
): Promise<RegionManagerResponse> => {
  try {
    const params = new URLSearchParams();
    
    if (filters?.level) params.append('level', filters.level);
    if (filters?.parent_id) params.append('parent_id', filters.parent_id);
    if (filters?.hierarchy_path) params.append('hierarchy_path', filters.hierarchy_path);
    if (filters?.selected_months) filters.selected_months.forEach(month => params.append('selected_months', month.toString()));
    if (filters?.selected_years) filters.selected_years.forEach(year => params.append('selected_years', year.toString()));
    if (filters?.selected_lob) filters.selected_lob.forEach(lob => params.append('selected_lob', lob));
    if (filters?.selected_products) {
      filters.selected_products.forEach(product => {
        const productValue = typeof product === 'string' ? product : product.mis_product;
        params.append('selected_products', productValue);
      });
    }
    if (filters?.selected_channels) filters.selected_channels.forEach(channel => params.append('selected_channels', channel));
    if (filters?.selected_sub_channels) filters.selected_sub_channels.forEach(subChannel => params.append('selected_sub_channels', subChannel));

    const response = await api.get(`/dashboard/region-hierarchy/${region}/summary${params.toString() ? `?${params.toString()}` : ''}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching region hierarchy summary:', error);
    throw error;
  }
};

export const fetchManagerAgencyDetails = async (
  region: string,
  managerId: string,
  filters?: { 
    selected_channels?: string[];
    selected_sub_channels?: string[];
    hierarchy_path?: string;
    selected_months?: number[];
    selected_years?: number[];
    selected_lob?: string[];
    selected_products?: (string | ProductInfo)[];
  }
): Promise<AgencyDetails> => {
  try {
    if (region && region !== 'all') {
      // Use GET endpoint for specific region
      const params = new URLSearchParams();
      if (filters?.selected_channels) {
        filters.selected_channels.forEach(channel => params.append('selected_channels', channel));
      }
      if (filters?.selected_sub_channels) {
        filters.selected_sub_channels.forEach(subChannel => params.append('selected_sub_channels', subChannel));
      }
      if (filters?.hierarchy_path) {
        params.append('hierarchy_path', filters.hierarchy_path);
      }
      if (filters?.selected_months) {
        filters.selected_months.forEach(month => params.append('selected_months', month.toString()));
      }
      if (filters?.selected_years) {
        filters.selected_years.forEach(year => params.append('selected_years', year.toString()));
      }
      if (filters?.selected_lob) {
        filters.selected_lob.forEach(lob => params.append('selected_lob', lob));
      }
      if (filters?.selected_products) {
        filters.selected_products.forEach(product => {
          const productValue = typeof product === 'string' ? product : product.mis_product;
          params.append('selected_products', productValue);
        });
      }

      const response = await api.get(
        `/dashboard/region-hierarchy/${region}/manager/${managerId}/agency${params.toString() ? `?${params.toString()}` : ''}`
      );
      return response.data;
    } else {
      // Use POST endpoint for all regions or when region is 'all'
      const payload: Record<string, any> = {
        manager_id: managerId
      };
      
      if (filters?.hierarchy_path) payload.hierarchy_path = filters.hierarchy_path;
      if (filters?.selected_months) payload.selected_months = filters.selected_months;
      if (filters?.selected_years) payload.selected_years = filters.selected_years;
      if (filters?.selected_lob) payload.selected_lob = filters.selected_lob;
      if (filters?.selected_products) {
        // Convert ProductInfo objects to their mis_product values
        payload.selected_products = filters.selected_products.map(product => 
          typeof product === 'string' ? product : product.mis_product
        );
      }
      if (filters?.selected_channels) payload.selected_channels = filters.selected_channels;
      if (filters?.selected_sub_channels) payload.selected_sub_channels = filters.selected_sub_channels;
      
      // Log the payload for debugging
      
      const response = await api.post(`/dashboard/region-hierarchy/manager/${managerId}/agency`, payload);
      return response.data;
    }
  } catch (error) {
    console.error('Error fetching manager agency details:', error);
    throw error;
  }
};

export interface ManagerProductHierarchyFilters {
  selected_months?: number[];
  selected_years?: number[];
  selected_lob?: string[];
  selected_products?: (string | ProductInfo)[];
  selected_channels?: string[];
  selected_sub_channels?: string[];
  hierarchy_path?: string;
}

export const fetchManagerProductHierarchy = async (
  region: string,
  managerId: string,
  startDate?: string,
  endDate?: string,
  parentId?: string,
  filters?: ManagerProductHierarchyFilters
): Promise<{ [key: string]: ProductHierarchy }> => {
  try {
    if (region && region !== 'all') {
      // Use GET endpoint for specific region
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (parentId) params.append('parent_id', parentId);
      if (filters?.hierarchy_path) params.append('hierarchy_path', filters.hierarchy_path);
      if (filters?.selected_months) filters.selected_months.forEach(month => params.append('selected_months', month.toString()));
      if (filters?.selected_years) filters.selected_years.forEach(year => params.append('selected_years', year.toString()));
      if (filters?.selected_lob) filters.selected_lob.forEach(lob => params.append('selected_lob', lob));
      if (filters?.selected_products) {
        filters.selected_products.forEach(product => {
          const productValue = typeof product === 'string' ? product : product.mis_product;
          params.append('selected_products', productValue);
        });
      }
      if (filters?.selected_channels) filters.selected_channels.forEach(channel => params.append('selected_channels', channel));
      if (filters?.selected_sub_channels) filters.selected_sub_channels.forEach(subChannel => params.append('selected_sub_channels', subChannel));

      const response = await api.get(`/dashboard/region-hierarchy/${region}/manager/${managerId}/products${params.toString() ? `?${params.toString()}` : ''}`);
      return response.data;
    } else {
      // Use POST endpoint for all regions or when region is 'all'
      const payload: Record<string, any> = {
        manager_id: managerId
      };
      
      if (startDate) payload.start_date = startDate;
      if (endDate) payload.end_date = endDate;
      if (parentId) payload.parent_id = parentId;
      if (filters?.hierarchy_path) payload.hierarchy_path = filters.hierarchy_path;
      if (filters?.selected_months) payload.selected_months = filters.selected_months;
      if (filters?.selected_years) payload.selected_years = filters.selected_years;
      if (filters?.selected_lob) payload.selected_lob = filters.selected_lob;
      if (filters?.selected_products) {
        // Convert ProductInfo objects to their mis_product values
        payload.selected_products = filters.selected_products.map(product => 
          typeof product === 'string' ? product : product.mis_product
        );
      }
      if (filters?.selected_channels) payload.selected_channels = filters.selected_channels;
      // Ensure selected_sub_channels is included in the payload
      if (filters?.selected_sub_channels) payload.selected_sub_channels = filters.selected_sub_channels;
      
      
      
      const response = await api.post(`/dashboard/region-hierarchy/manager/${managerId}/products`, payload);
      return response.data;
    }
  } catch (error) {
    console.error('Error fetching manager product hierarchy:', error);
    throw error;
  }
};

export const fetchProductStatusMatrix = async (
  status: string,
  product: string,
  startDate?: string,
  endDate?: string,
  selectedRegion?: string
): Promise<ProductStatusMatrix> => {
  try {
    const params = new URLSearchParams();
    params.append('status', status);
    params.append('product', product);
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (selectedRegion && selectedRegion !== 'all') params.append('selected_region', selectedRegion);

    
    const response = await api.get(`/dashboard/product-status-matrix?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching product-status matrix:', error);
    throw error;
  }
};

export const fetchChannelPerformanceMatrix = async (channel: string, startDate?: string, endDate?: string, selectedRegion?: string) => {
  try {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (selectedRegion && selectedRegion !== 'all') params.append('selected_region', selectedRegion);
    if (channel) params.append('channel', channel);

    
    const response = await api.get(`/dashboard/channel-performance-matrix${params.toString() ? `?${params.toString()}` : ''}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching channel performance matrix:', error);
    throw error;
  }
};

export const fetchChannelStatusMatrix = async (
  status: string,
  channel: string,
  startDate?: string,
  endDate?: string,
  selectedRegion?: string
): Promise<ChannelStatusMatrix> => {
  try {
    const params = new URLSearchParams();
    params.append('status', status);
    params.append('channel', channel);
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (selectedRegion && selectedRegion !== 'all') params.append('selected_region', selectedRegion);

    
    const response = await api.get(`/dashboard/channel-status-matrix?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching channel-status matrix:', error);
    throw error;
  }
};

export const fetchSubchannelStatusDistribution = async (
  currentMonthOnly: boolean = true,
  filters?: SelectedFilters
): Promise<SubchannelStatusResponse> => {
  try {
    const params = new URLSearchParams();
    params.append('current_month_only', currentMonthOnly.toString());
    
    // Add filter parameters if provided
    if (filters) {
      await buildFilterParams(params, filters);
    }
    
    const paramsString = params.toString();
    
    // Check if we have valid cached data for this specific request
    if (isValidCache('subchannelStatus', paramsString)) {
      return performanceCache['subchannelStatus']!.data;
    }
    
    const url = `${API_BASE_URL}/dashboard/distribution/subchannel-status?${paramsString}`;
    const response = await api.get(url);
    
    // Cache the response
    updateCache('subchannelStatus', response.data, paramsString);
    
    return response.data;
  } catch (error) {
    console.error('Error fetching subchannel status distribution:', error);
    throw error;
  }
};

export interface ProductHierarchyFilters {
  selected_months?: number[];
  selected_years?: number[];
  selected_lob?: string[];
  selected_products?: (string | ProductInfo)[];
  selected_channels?: string[];
  selected_sub_channels?: string[];
  selected_region?: string;
}

export const fetchAllProductsHierarchy = async (
  filters?: ProductHierarchyFilters,
  region?: string
): Promise<{ [key: string]: ProductHierarchy }> => {
  try {
    const payload: Record<string, any> = {};
    
    if (filters?.selected_months) payload.selected_months = filters.selected_months;
    if (filters?.selected_years) payload.selected_years = filters.selected_years;
    if (filters?.selected_lob) payload.selected_lob = filters.selected_lob;
    if (filters?.selected_products) {
      // Convert ProductInfo objects to their mis_product values
      payload.selected_products = filters.selected_products.map(product => 
        typeof product === 'string' ? product : product.mis_product
      );
    }
    if (filters?.selected_channels) payload.selected_channels = filters.selected_channels;
    if (filters?.selected_sub_channels) payload.selected_sub_channels = filters.selected_sub_channels;
    if (filters?.selected_region) payload.selected_region = filters.selected_region;
    
    // Also accept region as a separate parameter for backward compatibility
    if (region && region !== 'all') payload.selected_region = region;

    // Use the all regions endpoint without specifying a manager
    // This will get data for all regions and all BHs combined
    const response = await api.post('/dashboard/region-hierarchy/products', payload);
    return response.data;
  } catch (error) {
    console.error('Error fetching all products hierarchy:', error);
    throw error;
  }
};

export interface ChannelHierarchy {
  name: string;
  metrics: Metrics;
  subchannels?: { [key: string]: ChannelHierarchy };
}

export const fetchAllChannelsHierarchy = async (
  filters?: ProductHierarchyFilters,
  region?: string
): Promise<{ [key: string]: ChannelHierarchy }> => {
  try {
    const payload: Record<string, any> = {};
    
    if (filters?.selected_months) payload.selected_months = filters.selected_months;
    if (filters?.selected_years) payload.selected_years = filters.selected_years;
    if (filters?.selected_lob) payload.selected_lob = filters.selected_lob;
    if (filters?.selected_products) {
      // Convert ProductInfo objects to their mis_product values
      payload.selected_products = filters.selected_products.map(product => 
        typeof product === 'string' ? product : product.mis_product
      );
    }
    if (filters?.selected_channels) payload.selected_channels = filters.selected_channels;
    if (filters?.selected_sub_channels) payload.selected_sub_channels = filters.selected_sub_channels;
    if (filters?.selected_region) payload.selected_region = filters.selected_region;
    
    // Also accept region as a separate parameter for backward compatibility
    if (region && region !== 'all') payload.selected_region = region;

    // Use the all regions endpoint without specifying a manager
    // This will get data for all regions and all BHs combined
    const response = await api.post('/dashboard/region-hierarchy/channels', payload);
    return response.data;
  } catch (error) {
    console.error('Error fetching all channels hierarchy:', error);
    throw error;
  }
};

export interface OverallPerformance {
  total_leads: string;
  converted_leads: string;
  conversion_rate: string;
}

export interface LOBPerformance {
  lob: string;
  total_leads: string;
  converted_leads: string;
  conversion_rate: string;
}

// Helper function to build URL parameters from filter objects
const buildFilterParams = async (params: URLSearchParams, filters?: SelectedFilters) => {
  // Get caller function name from stack trace
  const stackTrace = new Error().stack;
  const callerName = stackTrace?.split('\n')[2]?.trim()?.split(' ')[1] || 'unknown';
  
  if (filters) {
    if (filters.selectedMonth) {
      params.append('month', filters.selectedMonth);
    }

    if (filters.selectedYear) {
      try {
        const options = cachedFilterOptions || await fetchFilterOptions();
        const selectedYearValue = Array.isArray(filters.selectedYear)
          ? filters.selectedYear[0]
          : filters.selectedYear;
    
        // Try to find matching year option from cached options
        let yearOption = options?.years?.find(y => y.id === selectedYearValue);
    
        // If not found, create year option manually
        if (!yearOption && /^\d{4}$/.test(selectedYearValue)) {
          yearOption = {
            id: `${selectedYearValue}_${Number(selectedYearValue) + 1}`,
            name: `FY ${selectedYearValue}-${(Number(selectedYearValue) + 1).toString().slice(-2)}`,
            start_year: Number(selectedYearValue),
            end_year: Number(selectedYearValue) + 1
          };
        }
    
        
        if (yearOption && filters.selectedMonth) {
          const selectedMonth = parseInt(filters.selectedMonth);
          const calendarYear =
            selectedMonth >= 1 && selectedMonth <= 3
              ? yearOption.end_year.toString()
              : yearOption.start_year.toString();
    
          params.append('year', calendarYear);
        } else if (yearOption) {
          params.append('year', yearOption.id);
        }
      } catch (error) {
        console.error('Error getting year details:', error);
        const fallbackYear = Array.isArray(filters.selectedYear)
          ? filters.selectedYear[0]
          : filters.selectedYear;
        params.append('year', fallbackYear);
      }
    }

    
    if (filters.selectedLOB?.length) {
      filters.selectedLOB.forEach(lob => params.append('lob', lob));
    }
    if (filters.selectedProducts?.length) {
      filters.selectedProducts.forEach(product => params.append('product', product));
    }
    if (filters.selectedChannels?.length) {
      filters.selectedChannels.forEach(channel => params.append('channel_id', channel));
    }
    if (filters.selectedSubChannels?.length) {
      filters.selectedSubChannels.forEach(subChannel => params.append('subchannel_id', subChannel));
    }
    if (filters.selectedSubProducts?.length) {
      filters.selectedSubProducts.forEach(subProduct => params.append('sub_product', subProduct));
    }
    if (filters.selectedRegion) {
      params.append('region_id', filters.selectedRegion);
    }
    if (filters.dateRange?.startDate) {
      params.append('start_date', filters.dateRange.startDate.toISOString().split('T')[0]);
    }
    if (filters.dateRange?.endDate) {
      params.append('end_date', filters.dateRange.endDate.toISOString().split('T')[0]);
    }
  }
  return params;
};

export const fetchOverallPerformance = async (
  currentMonthOnly: boolean = true,
  filters?: SelectedFilters
): Promise<OverallPerformance> => {
  try {
    const params = new URLSearchParams();
    params.append('current_month_only', currentMonthOnly.toString());
    
    // Add filter parameters if provided
    if (filters) {
      await buildFilterParams(params, filters);
    }
    
    const paramsString = params.toString();
    
    // Check if we have valid cached data for this specific request
    if (isValidCache('overall', paramsString)) {
      return performanceCache['overall']!.data;
    }
    
    const url = `${API_BASE_URL}/dashboard/performance/overall?${paramsString}`;
    const response = await api.get(url);
    
    // Cache the response
    updateCache('overall', response.data, paramsString);
    
    return response.data;
  } catch (error) {
    console.error('Error fetching overall performance:', error);
    throw error;
  }
};

export const fetchLOBPerformance = async (
  currentMonthOnly: boolean = true,
  filters?: SelectedFilters
): Promise<LOBPerformance[]> => {
  try {
    const params = new URLSearchParams();
    params.append('current_month_only', currentMonthOnly.toString());
    
    // Add filter parameters if provided
    if (filters) {
      await buildFilterParams(params, filters);
    }
    
    const paramsString = params.toString();
    
    // Check if we have valid cached data for this specific request
    if (isValidCache('lob', paramsString)) {
      return performanceCache['lob']!.data;
    }
    
    const url = `${API_BASE_URL}/dashboard/performance/lob?${paramsString}`;
    const response = await api.get(url);
    
    // Cache the response
    updateCache('lob', response.data, paramsString);
    
    return response.data;
  } catch (error) {
    console.error('Error fetching LOB performance:', error);
    throw error;
  }
};

export const fetchRegionMap = async (
  filters?: SelectedFilters
): Promise<RegionMapResponse> => {
  try {
    const params = new URLSearchParams();
    params.append('current_month_only', 'true');
    
    // Add filter parameters if provided
    if (filters) {
      // Create a copy of filters without selectedRegion
      const filtersWithoutRegion = { ...filters };
      delete filtersWithoutRegion.selectedRegion;
      
      await buildFilterParams(params, filtersWithoutRegion);
    }
    
    const paramsString = params.toString();
    
    // Check if we have valid cached data for this specific request
    if (isValidCache('regionMap', paramsString)) {
      return performanceCache['regionMap']!.data;
    }
    
    const url = `${API_BASE_URL}/dashboard/region-map?${paramsString}`;
    const response = await api.get(url);
    
    // Cache the response
    updateCache('regionMap', response.data, paramsString);
    
    return response.data;
  } catch (error) {
    console.error('Error fetching region map data:', error);
    throw error;
  }
};

export const fetchProductChannelDistribution = async (
  currentMonthOnly: boolean = true,
  filters?: SelectedFilters
): Promise<ProductChannelResponse> => {
  try {
    const params = new URLSearchParams();
    params.append('current_month_only', currentMonthOnly.toString());
    
    // Add filter parameters if provided
    if (filters) {
      await buildFilterParams(params, filters);
    }
    
    const paramsString = params.toString();
    
    // Check if we have valid cached data for this specific request
    if (isValidCache('productChannel', paramsString)) {
      return performanceCache['productChannel']!.data;
    }
    
    const url = `${API_BASE_URL}/dashboard/distribution/product-channel?${paramsString}`;
    const response = await api.get(url);
    
    // Cache the response
    updateCache('productChannel', response.data, paramsString);
    
    return response.data;
  } catch (error) {
    console.error('Error fetching product channel distribution:', error);
    throw error;
  }
};

export const fetchChannelProductDistribution = async (
  currentMonthOnly: boolean = true,
  filters?: SelectedFilters
): Promise<ChannelProductResponse> => {
  try {
    const params = new URLSearchParams();
    params.append('current_month_only', currentMonthOnly.toString());
    
    // Add filter parameters if provided
    if (filters) {
      await buildFilterParams(params, filters);
    }
    
    const paramsString = params.toString();
    
    // Check if we have valid cached data for this specific request
    if (isValidCache('channelProduct', paramsString)) {
      return performanceCache['channelProduct']!.data;
    }
    
    const url = `${API_BASE_URL}/dashboard/distribution/channel-product?${paramsString}`;
    const response = await api.get(url);
    
    // Cache the response
    updateCache('channelProduct', response.data, paramsString);
    
    return response.data;
  } catch (error) {
    console.error('Error fetching channel product distribution:', error);
    throw error;
  }
};

export const fetchProductStatusDistribution = async (
  currentMonthOnly: boolean = true,
  filters?: SelectedFilters
): Promise<ProductStatusResponse> => {
  try {
    const params = new URLSearchParams();
    params.append('current_month_only', currentMonthOnly.toString());
    
    // Add filter parameters if provided
    if (filters) {
      await buildFilterParams(params, filters);
    }
    
    const paramsString = params.toString();
    
    // Check if we have valid cached data for this specific request
    if (isValidCache('productStatus', paramsString)) {
      return performanceCache['productStatus']!.data;
    }
    
    const url = `${API_BASE_URL}/dashboard/distribution/product-status?${paramsString}`;
    const response = await api.get(url);
    
    // Cache the response
    updateCache('productStatus', response.data, paramsString);
    
    return response.data;
  } catch (error) {
    console.error('Error fetching product status distribution:', error);
    throw error;
  }
};

export const fetchChannelStatusDistribution = async (
  currentMonthOnly: boolean = true,
  filters?: SelectedFilters
): Promise<ChannelStatusResponse> => {
  try {
    const params = new URLSearchParams();
    params.append('current_month_only', currentMonthOnly.toString());
    
    // Add filter parameters if provided
    if (filters) {
      await buildFilterParams(params, filters);
    }
    
    const paramsString = params.toString();
    
    // Check if we have valid cached data for this specific request
    if (isValidCache('channelStatus', paramsString)) {
      return performanceCache['channelStatus']!.data;
    }
    
    const url = `${API_BASE_URL}/dashboard/distribution/channel-status?${paramsString}`;
    const response = await api.get(url);
    
    // Cache the response
    updateCache('channelStatus', response.data, paramsString);
    
    return response.data;
  } catch (error) {
    console.error('Error fetching channel status distribution:', error);
    throw error;
  }
};

export async function fetchProductConversionRate(
  currentMonthOnly: boolean = true,
  filters?: SelectedFilters
): Promise<ProductConversionResponse> {
  try {
    const params = new URLSearchParams();
    params.append('current_month_only', currentMonthOnly.toString());
    
    // Add filter parameters if provided
    if (filters) {
      await buildFilterParams(params, filters);
    }
    
    const paramsString = params.toString();
    
    // Check if we have valid cached data for this specific request
    if (isValidCache('productConversion', paramsString)) {
      return performanceCache['productConversion']!.data;
    }
    
    const url = `${API_BASE_URL}/dashboard/conversion-rate/product?${paramsString}`;
    const response = await api.get(url);
    
    // Cache the response
    updateCache('productConversion', response.data, paramsString);
    
    return response.data;
  } catch (error) {
    console.error('Error fetching product conversion rate:', error);
    throw error;
  }
}

export async function fetchChannelConversionRate(
  currentMonthOnly: boolean = true,
  filters?: SelectedFilters
): Promise<ChannelConversionResponse> {
  try {
    const params = new URLSearchParams();
    params.append('current_month_only', currentMonthOnly.toString());
    
    // Add filter parameters if provided
    if (filters) {
      await buildFilterParams(params, filters);
    }
    
    const paramsString = params.toString();
    
    // Check if we have valid cached data for this specific request
    if (isValidCache('channelConversion', paramsString)) {
      return performanceCache['channelConversion']!.data;
    }
    
    const url = `${API_BASE_URL}/dashboard/conversion-rate/channel?${paramsString}`;
    const response = await api.get(url);
    
    // Cache the response
    updateCache('channelConversion', response.data, paramsString);
    
    return response.data;
  } catch (error) {
    console.error('Error fetching channel conversion rate:', error);
    throw error;
  }
}

export async function fetchSubchannelConversionRate(
  currentMonthOnly: boolean = true,
  filters?: SelectedFilters
): Promise<SubchannelConversionResponse> {
  try {
    const params = new URLSearchParams();
    params.append('current_month_only', currentMonthOnly.toString());
    
    // Add filter parameters if provided
    if (filters) {
      await buildFilterParams(params, filters);
    }
    
    const paramsString = params.toString();
    
    // Check if we have valid cached data for this specific request
    if (isValidCache('subchannelConversion', paramsString)) {
      return performanceCache['subchannelConversion']!.data;
    }
    
    const url = `${API_BASE_URL}/dashboard/conversion-rate/subchannel?${paramsString}`;
    const response = await api.get(url);
    
    // Cache the response
    updateCache('subchannelConversion', response.data, paramsString);
    
    return response.data;
  } catch (error) {
    console.error('Error fetching subchannel conversion rate:', error);
    throw error;
  }
}

export async function fetchChannelPerformanceBreakdown(
  filters?: SelectedFilters
): Promise<ChannelPerformanceBreakdownResponse> {
  try {
    const params = new URLSearchParams();
    
    // Add filter parameters if provided
    if (filters) {
      await buildFilterParams(params, filters);
    }
    
    const paramsString = params.toString();
    
    // Check if we have valid cached data for this specific request
    if (isValidCache('channelBreakdown', paramsString)) {
      return performanceCache['channelBreakdown']!.data;
    }
    
    const url = `${API_BASE_URL}/dashboard/performance/channel-breakdown?${paramsString}`;
    const response = await api.get(url);
    
    // Cache the response
    updateCache('channelBreakdown', response.data, paramsString);
    
    return response.data;
  } catch (error) {
    console.error('Error fetching channel performance breakdown:', error);
    throw error;
  }
}

export async function fetchSubchannelPerformanceBreakdown(
  filters?: SelectedFilters
): Promise<SubchannelPerformanceResponse> {
  try {
    const params = new URLSearchParams();
    
    // Add filter parameters if provided
    if (filters) {
      await buildFilterParams(params, filters);
    }
    
    const url = `${API_BASE_URL}/dashboard/performance/subchannel-breakdown?${params.toString()}`;
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching subchannel performance breakdown:', error);
    throw error;
  }
}

export async function fetchProductPerformanceBreakdown(
  filters?: SelectedFilters
): Promise<ProductPerformanceResponse> {
  try {
    const params = new URLSearchParams();
    
    // Add filter parameters if provided
    if (filters) {
      await buildFilterParams(params, filters);
    }
    
    const url = `${API_BASE_URL}/dashboard/performance/product-breakdown?${params.toString()}`;
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching product performance breakdown:', error);
    throw error;
  }
}

export interface DependentFilterOptions {
  channels: string[];
  subchannels: string[];
  product_groups: string[];
  lobs: string[];
}

export const fetchDependentFilters = async (
  channel?: string | string[],
  subchannel?: string | string[],
  product_group?: string | string[],
  sub_product?: string | string[],
  lob?: string | string[],
  year?: string
): Promise<FilterOptions> => {
  const params = new URLSearchParams();
  
  if (channel) {
    if (Array.isArray(channel)) {
      channel.forEach(c => params.append('channel_id', c));
    } else {
      params.append('channel_id', channel);
    }
  }
  
  if (subchannel) {
    if (Array.isArray(subchannel)) {
      subchannel.forEach(sc => params.append('subchannel_id', sc));
    } else {
      params.append('subchannel_id', subchannel);
    }
  }
  
  if (product_group) {
    if (Array.isArray(product_group)) {
      product_group.forEach(pg => params.append('product', pg));
    } else {
      params.append('product', product_group);
    }
  }

  if (sub_product) {  
    if (Array.isArray(sub_product)) {
      sub_product.forEach(sp => params.append('sub_product', sp));
    } else {
      params.append('sub_product', sub_product);
    }
  }
  if (lob) {
    if (Array.isArray(lob)) {
      lob.forEach(l => params.append('lob', l));
    } else {
      params.append('lob', lob);
    }
  }
  
  if (year) params.append('year', year);

  const response = await api.get(`/dashboard/filters/dependent?${params.toString()}`);
  return {
    months: response.data.months,
    years: response.data.years,
    channel: response.data.channel,
    subchannel: response.data.subchannel,
    product_group: response.data.product_group,
    sub_product: response.data.sub_product || [],
    lob: response.data.lob
  };
};

// Interface for RegionMap response
export interface RegionMapResponse {
  region_data: {
    [key: string]: {
      total_leads: string;
      converted: string;
      conversion_rate: string;
    };
  };
}

// Interface for ProductChannel response
export interface ProductChannelData {
  product_group: string;
  total_leads: string;
  converted_leads: string;
  channels: {
    [key: string]: {
      total_leads: string;
      converted_leads: string;
      conversion_rate: string;
      contribution_rate: string;
    };
  };
  conversion_rate: string;
}

export interface ProductChannelResponse {
  products: ProductChannelData[];
}

// Interface for ChannelProduct response
export interface ChannelProductData {
  channel: string;
  total_leads: string;
  converted_leads: string;
  products: {
    [key: string]: {
      product_group: string;
      total_leads: string;
      converted_leads: string;
      conversion_rate: string;
      contribution_rate: string;
    };
  };
  conversion_rate: string;
}

export interface ChannelProductResponse {
  channels: ChannelProductData[];
}

// Interface for ProductStatus response
export interface ProductStatusData {
  product_group: string;
  total_leads: string;
  converted_leads: string;
  statuses: {
    [key: string]: {
      status: string;
      total_leads: string;
      percentage: string;
      conversion_rate: string;
      converted_leads: string;
    };
  };
}

export interface ProductStatusResponse {
  products: ProductStatusData[];
}

// Interface for ChannelStatus response
export interface ChannelStatusData {
  channel: string;
  total_leads: string;
  statuses: {
    [key: string]: {
      status: string;
      total_leads: string;
      percentage: string;
      conversion_rate: string;
      converted_leads: string;
    };
  };
}

export interface ChannelStatusResponse {
  channels: ChannelStatusData[];
}

// Interface for SubchannelStatus response
export interface SubchannelStatusData {
  subchannel: string;
  total_leads: string;
  statuses: {
    [key: string]: {
      status: string;
      total_leads: string;
      percentage: string;
      conversion_rate: string;
      converted_leads: string;
    };
  };
}

export interface SubchannelStatusResponse {
  subchannels: SubchannelStatusData[];
}

// Interface for ProductConversion response
export interface ProductConversionData {
  product_group: string;
  total_leads: string;
  converted_leads: string;
  conversion_rate: string;
}

export interface ProductConversionResponse {
  products: ProductConversionData[];
}

// Interface for ChannelConversion response
export interface ChannelConversionData {
  channel: string;
  total_leads: string;
  converted_leads: string;
  conversion_rate: string;
}

export interface ChannelConversionResponse {
  channels: ChannelConversionData[];
}

// Interface for SubchannelConversion response
export interface SubchannelConversionData {
  subchannel: string;
  channel: string;
  total_leads: string;
  converted_leads: string;
  conversion_rate: string;
}

export interface SubchannelConversionResponse {
  subchannels: SubchannelConversionData[];
}

// Interface for ChannelPerformanceBreakdown
export interface PerformanceMetrics {
  total_leads: string;
  converted: string;
  conversion_rate: string;
}

export interface SubProductItem {
  sub_product: string;
  ftm: PerformanceMetrics;
  aod: PerformanceMetrics;
  target?: number;
}

export interface ProductItem {
  product: string;
  ftm: PerformanceMetrics;
  aod: PerformanceMetrics;
  sub_products: SubProductItem[];
  target?: number;
}

export interface LOBItem {
  lob: string;
  ftm: PerformanceMetrics;
  aod: PerformanceMetrics;
  products: ProductItem[];
  target?: number;
}

export interface SubchannelItem {
  subchannel: string;
  ftm: PerformanceMetrics;
  aod: PerformanceMetrics;
  lobs?: LOBItem[];
  target?: number;
}

export interface ChannelBreakdown {
  channel: string;
  ftm: PerformanceMetrics;
  aod: PerformanceMetrics;
  sub_channels: SubchannelItem[];
  target?: number;
}

export interface ChannelPerformanceBreakdownResponse {
  channels: ChannelBreakdown[];
}

// Interface for SubchannelPerformance response
export interface SubchannelPerformanceItem {
  subchannel: string;
  channel: string;
  ftm: {
    total_leads: string;
    converted: string;
    conversion_rate: string;
  };
  aod: {
    total_leads: string;
    converted: string;
    conversion_rate: string;
  };
}

export interface SubchannelPerformanceResponse {
  subchannels: SubchannelPerformanceItem[];
}

// Interface for ProductPerformance response
export interface SubProductItem {
  sub_product: string;
  ftm: {
    total_leads: string;
    converted: string;
    conversion_rate: string;
  };
  aod: {
    total_leads: string;
    converted: string;
    conversion_rate: string;
  };
}

export interface ProductItem {
  product_name: string;
  ftm: {
    total_leads: string;
    converted: string;
    conversion_rate: string;
  };
  aod: {
    total_leads: string;
    converted: string;
    conversion_rate: string;
  };
  sub_products: SubProductItem[];
}

export interface ProductGroupItem {
  product_group: string | null;
  ftm: {
    total_leads: string;
    converted: string;
    conversion_rate: string;
  };
  aod: {
    total_leads: string;
    converted: string;
    conversion_rate: string;
  };
  products: ProductItem[];
}

export interface ProductPerformanceResponse {
  products: ProductGroupItem[];
}

// Manager Breakdown API
export interface ManagerBreakdownSubProduct {
  sub_product: string;
  ftm: {
    total_leads: string | number;
    converted: string | number;
    conversion_rate: string | number;
  };
  aod: {
    total_leads: string | number;
    converted: string | number;
    conversion_rate: string | number;
  };
}

export interface ManagerBreakdownProduct {
  product_name: string;
  ftm: {
    total_leads: string | number;
    converted: string | number;
    conversion_rate: string | number;
  };
  aod: {
    total_leads: string | number;
    converted: string | number;
    conversion_rate: string | number;
  };
  sub_products: ManagerBreakdownSubProduct[];
}

export interface ManagerBreakdownProductGroup {
  product_group: string | null;
  ftm: {
    total_leads: string | number;
    converted: string | number;
    conversion_rate: string | number;
  };
  aod: {
    total_leads: string | number;
    converted: string | number;
    conversion_rate: string | number;
  };
  products: ManagerBreakdownProduct[];
}

export interface ManagerBreakdownSubChannel {
  subchannel: string;
  ftm: {
    total_leads: string | number;
    converted: string | number;
    conversion_rate: string | number;
  };
  aod: {
    total_leads: string | number;
    converted: string | number;
    conversion_rate: string | number;
  };
}

export interface ManagerBreakdownChannel {
  channel: string;
  ftm: {
    total_leads: string | number;
    converted: string | number;
    conversion_rate: string | number;
  };
  aod: {
    total_leads: string | number;
    converted: string | number;
    conversion_rate: string | number;
  };
  sub_channels: ManagerBreakdownSubChannel[];
}

export interface ManagerBreakdownSubordinate {
  employee_id: string;
  employee_name: string;
  employee_role: string;
  role_code: string;
  hierarchy_path: string;
  ftm: {
    total_leads: string | number;
    converted: string | number;
    conversion_rate: string | number;
  };
  aod: {
    total_leads: string | number;
    converted: string | number;
    conversion_rate: string | number;
  };
}

export interface ManagerBreakdownResponse {
  employee_id: string;
  employee_name: string;
  employee_role: string;
  ftm: {
    total_leads: string | number;
    converted: string | number;
    conversion_rate: string | number;
  };
  aod: {
    total_leads: string | number;
    converted: string | number;
    conversion_rate: string | number;
  };
  products: ManagerBreakdownProductGroup[];
  channels: ManagerBreakdownChannel[];
  subordinates: ManagerBreakdownSubordinate[];
}

export const fetchManagerBreakdown = async (
  employeeId?: string,
  role?: string,
  hierarchyPath?: string,
  filters?: SelectedFilters
): Promise<ManagerBreakdownResponse> => {
  try {
    const params = new URLSearchParams();
    // Only append employee_id if it's defined and not empty
    if (employeeId !== undefined && employeeId !== '') {
      params.append('target_employee_id', employeeId);
    }
    if (role !== undefined && role !== '') {
      params.append('role', role);
    }
    if (hierarchyPath !== undefined && hierarchyPath !== '') {
      params.append('hierarchy_path', hierarchyPath);
    }

    // Add filter parameters if provided
    if (filters) {
      await buildFilterParams(params, filters);
    }
    
    const paramsString = params.toString();
    
    // Check if we have valid cached data for this specific request
    if (isValidCache('managerBreakdown', paramsString)) {
      return performanceCache['managerBreakdown']!.data;
    }

    
    const url = `${API_BASE_URL}/dashboard/performance/manager-breakdown?${paramsString}`;
    
    const response = await api.get(url);
    
    // Cache the response
    updateCache('managerBreakdown', response.data, paramsString);
    
    return response.data;
  } catch (error) {
    console.error('Error fetching manager breakdown:', error);
    throw error;
  }
};

// ============================================================================
// SMS TRIGGERING API FUNCTIONS
// ============================================================================

import { 
  SmsFilterOptions, 
  SmsTriggerFilters, 
  SmsTriggerPreview, 
  SmsTriggerSendResponse, 
  SmsTriggerStatus,
  SmsPolicyFilters,
  PoliciesResponse,
  SmsHistoryResponse,
  PolicyLinkMetricsResponse
} from '@/types/sms';

/**
 * Get available filter options for SMS triggering
 * Returns distinct values from renewal_notice_data
 */
export const getSmsFilterOptions = async (): Promise<SmsFilterOptions> => {
  try {
    const response = await api.get('/sms/trigger/filter-options');
    return response.data;
  } catch (error: any) {
    const detail = error?.response?.data?.detail || 'Failed to get SMS filter options';
    throw new Error(detail);
  }
};

/**
 * Get dependent filter options based on selected channel(s), sub-channel(s), LOB(s), and/or product_name(s)
 * Returns union of sub-channels filtered by channel(s), union of LOBs filtered by sub-channel(s), 
 * union of product names filtered by LOB(s), and/or union of channels filtered by product_name(s)
 */
export const getSmsDependentFilterOptions = async (
  channel?: string | string[], 
  sub_channel?: string | string[], 
  lob?: string | string[],
  product_name?: string | string[]
): Promise<{ sub_channels: string[]; lobs: string[]; product_names: string[]; channels: string[] }> => {
  try {
    const params = new URLSearchParams();
    // Handle arrays - append multiple values for each filter
    if (channel) {
      if (Array.isArray(channel)) {
        channel.forEach(ch => params.append('channel', ch));
      } else {
        params.append('channel', channel);
      }
    }
    if (sub_channel) {
      if (Array.isArray(sub_channel)) {
        sub_channel.forEach(sc => params.append('sub_channel', sc));
      } else {
        params.append('sub_channel', sub_channel);
      }
    }
    if (lob) {
      if (Array.isArray(lob)) {
        lob.forEach(l => params.append('lob', l));
      } else {
        params.append('lob', lob);
      }
    }
    if (product_name) {
      if (Array.isArray(product_name)) {
        product_name.forEach(pn => params.append('product_name', pn));
      } else {
        params.append('product_name', product_name);
      }
    }
    const response = await api.get(`/sms/trigger/filter-options/dependent?${params.toString()}`);
    return {
      sub_channels: response.data.sub_channels || [],
      lobs: response.data.lobs || [],
      product_names: response.data.product_names || [],
      channels: response.data.channels || []
    };
  } catch (error: any) {
    const detail = error?.response?.data?.detail || 'Failed to get SMS dependent filter options';
    throw new Error(detail);
  }
};

/**
 * Preview policies that match the filter criteria
 * Returns count and sample of matching policies for confirmation
 */
export const getSmsTriggerPreview = async (filters: SmsTriggerFilters): Promise<SmsTriggerPreview> => {
  try {
    const response = await api.post('/sms/trigger/preview', filters);
    return response.data;
  } catch (error: any) {
    const detail = error?.response?.data?.detail || 'Failed to preview SMS trigger';
    throw new Error(detail);
  }
};

/**
 * Trigger SMS for policies matching the filter criteria
 * Handles both individual and bulk scenarios
 */
export const sendSmsTrigger = async (filters: SmsTriggerFilters): Promise<SmsTriggerSendResponse> => {
  try {
    const response = await api.post('/sms/trigger/send', filters);
    return response.data;
  } catch (error: any) {
    const detail = error?.response?.data?.detail || 'Failed to trigger SMS';
    throw new Error(detail);
  }
};

/**
 * Get SMS processing status for a batch
 * Returns current status and progress information
 */
export const getSmsTriggerStatus = async (batchId: string): Promise<SmsTriggerStatus> => {
  try {
    const response = await api.get(`/sms/trigger/status/${batchId}`);
    return response.data;
  } catch (error: any) {
    const detail = error?.response?.data?.detail || 'Failed to get SMS status';
    throw new Error(detail);
  }
};

/**
 * Get SMS trigger history for a policy
 */
export const getSmsHistory = async (policyNumber: string): Promise<SmsHistoryResponse> => {
  try {
    const encoded = encodeURIComponent(policyNumber);
    const response = await api.get(`/sms/history/${encoded}`);
    return response.data;
  } catch (error: any) {
    const detail = error?.response?.data?.detail || 'Failed to fetch SMS history';
    throw new Error(detail);
  }
};

/**
 * Get link click metrics for a specific policy
 * Returns click statistics for all SMS links (PDF and renewal) associated with the policy
 */
export const getPolicyLinkMetrics = async (policyNumber: string): Promise<PolicyLinkMetricsResponse> => {
  try {
    const encoded = encodeURIComponent(policyNumber);
    const response = await api.get(`/sms/history/links/${encoded}`);
    return response.data;
  } catch (error: any) {
    const detail = error?.response?.data?.detail || 'Failed to fetch policy link metrics';
    throw new Error(detail);
  }
};

/**
 * Get policies for SMS selection with filtering
 * Returns paginated list of policies for individual selection
 */
export const getPoliciesForSmsSelection = async (filters: SmsPolicyFilters): Promise<PoliciesResponse> => {
  try {
    const params = new URLSearchParams();
    
    // Add filter parameters
    if (filters.lob) {
      params.append('lob', filters.lob);
    }
    if (filters.product_code) {
      params.append('product_code', filters.product_code);
    }
    if (filters.product_name) {
      params.append('product_name', filters.product_name);
    }
    // Handle date filtering: convert date_range or red_from/red_to to start_date/end_date
    // Priority: red_from/red_to > date_range calculation
    if (filters.red_from || filters.red_to) {
      // If RED date is manually selected, use red_from/red_to as start_date/end_date
      const redDate = filters.red_from || filters.red_to;
      if (redDate) {
        params.append('start_date', redDate);
        params.append('end_date', redDate);
      }
    } else if (filters.date_range && filters.date_range !== 'custom') {
      // If date_range is set, calculate RED date and use it for both start and end (exact match)
      const calculatedDate = calculateRedDateFromRange(filters.date_range);
      if (calculatedDate) {
        params.append('start_date', calculatedDate);
        params.append('end_date', calculatedDate);
      }
    }
    if (filters.policy_number) {
      params.append('policy_number', filters.policy_number);
    }
    if (filters.channel) {
      // Handle single value (string) or array for backward compatibility
      if (Array.isArray(filters.channel)) {
        filters.channel.forEach(ch => params.append('channel', ch));
      } else {
        params.append('channel', filters.channel);
      }
    }
    if (filters.sub_channel) {
      // Handle single value (string) or array for backward compatibility
      if (Array.isArray(filters.sub_channel)) {
        filters.sub_channel.forEach(sc => params.append('sub_channel', sc));
      } else {
        params.append('sub_channel', filters.sub_channel);
      }
    }
    if (filters.customer_mobile) {
      params.append('customer_mobile', filters.customer_mobile);
    }
    if (filters.search) {
      params.append('search', filters.search);
    }
    if (filters.month) {
      params.append('month', filters.month);
    }
    if (filters.page) {
      params.append('page', filters.page.toString());
    }
    if (filters.page_size) {
      params.append('page_size', filters.page_size.toString());
    }
    
    const url = `/renewal-dashboard/policies${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await api.get(url);
    return response.data;
  } catch (error: any) {
    const detail = error?.response?.data?.detail || 'Failed to get policies for selection';
    throw new Error(detail);
  }
};

/**
 * Trigger SMS for individually selected policies
 * Sends policy_numbers to backend (backend maps to IDs)
 */
export const triggerIndividualSms = async (
  policyNumbers: string[], 
  smsType: 'notice_only' | 'notice_with_link' = 'notice_only',
  triggeredBy?: string
): Promise<SmsTriggerSendResponse> => {
  try {
    const response = await api.post('/sms/trigger/trigger-individual', {
      policy_numbers: policyNumbers,
      sms_type: smsType,
      triggered_by: triggeredBy
    });
    return response.data;
  } catch (error: any) {
    const detail = error?.response?.data?.detail || 'Failed to trigger individual SMS';
    throw new Error(detail);
  }
};

/**
 * Download error log for failed SMS in a batch
 * Downloads CSV file with error details
 */
export const downloadSmsErrorLog = async (batchId: string): Promise<void> => {
  try {
    const response = await api.get(`/sms/export-errors/${batchId}`, {
      responseType: 'blob'
    });
    
    // Create a download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    
    // Extract filename from content-disposition header or use default
    const contentDisposition = response.headers['content-disposition'];
    let filename = `sms_errors_${batchId}_${new Date().getTime()}.csv`;
    
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1];
      }
    }
    
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error: any) {
    const detail = error?.response?.data?.detail || 'Failed to download error log';
    throw new Error(detail);
  }
};

/**
 * Download complete batch summary
 * Downloads CSV file with all SMS records for the batch
 */
export const downloadSmsBatchSummary = async (batchId: string): Promise<void> => {
  try {
    const response = await api.get(`/sms/export-batch/${batchId}`, {
      responseType: 'blob'
    });
    
    // Create a download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    
    // Extract filename from content-disposition header or use default
    const contentDisposition = response.headers['content-disposition'];
    let filename = `sms_batch_${batchId}_${new Date().getTime()}.csv`;
    
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1];
      }
    }
    
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error: any) {
    const detail = error?.response?.data?.detail || 'Failed to download batch summary';
    throw new Error(detail);
  }
};

// ============================================================================
// SMS SCHEDULER API FUNCTIONS
// ============================================================================

import { 
  SchedulerConfig, 
  SchedulerConfigCreate, 
  SchedulerConfigUpdate,
  SchedulerLog,
  SchedulerLogFilters,
  SchedulerStatus
} from '@/types/smsScheduler';

/**
 * Get all scheduler configurations
 * Returns list of scheduler configurations with optional filtering
 */
export const getSchedulerConfigs = async (isActive?: boolean): Promise<SchedulerConfig[]> => {
  try {
    const params = new URLSearchParams();
    if (isActive !== undefined) {
      params.append('is_active', isActive.toString());
    }
    
    const url = `/sms/scheduler/config${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await api.get(url);
    return response.data;
  } catch (error: any) {
    const detail = error?.response?.data?.detail || 'Failed to get scheduler configurations';
    throw new Error(detail);
  }
};

/**
 * Create a new scheduler configuration
 * Creates a new scheduler configuration and registers the job if active
 */
export const createSchedulerConfig = async (config: SchedulerConfigCreate): Promise<SchedulerConfig> => {
  try {
    const response = await api.post('/sms/scheduler/config', config);
    return response.data;
  } catch (error: any) {
    const detail = error?.response?.data?.detail || 'Failed to create scheduler configuration';
    throw new Error(detail);
  }
};

/**
 * Update an existing scheduler configuration
 * Updates the configuration and re-registers the job if active
 */
export const updateSchedulerConfig = async (id: number, config: SchedulerConfigUpdate): Promise<SchedulerConfig> => {
  try {
    const response = await api.put(`/sms/scheduler/config/${id}`, config);
    return response.data;
  } catch (error: any) {
    const detail = error?.response?.data?.detail || 'Failed to update scheduler configuration';
    throw new Error(detail);
  }
};

/**
 * Delete a scheduler configuration
 * Deletes the configuration and unregisters the job
 */
export const deleteSchedulerConfig = async (id: number): Promise<{message: string}> => {
  try {
    const response = await api.delete(`/sms/scheduler/config/${id}`);
    return response.data;
  } catch (error: any) {
    const detail = error?.response?.data?.detail || 'Failed to delete scheduler configuration';
    throw new Error(detail);
  }
};

/**
 * Get scheduler execution logs with optional filters
 * Returns filtered list of scheduler execution logs
 */
export const getSchedulerLogs = async (filters: SchedulerLogFilters): Promise<SchedulerLog[]> => {
  try {
    const params = new URLSearchParams();
    
    if (filters.config_id !== undefined) {
      params.append('config_id', filters.config_id.toString());
    }
    if (filters.schedule_name) {
      params.append('schedule_name', filters.schedule_name);
    }
    if (filters.status) {
      params.append('status', filters.status);
    }
    if (filters.start_date) {
      params.append('start_date', filters.start_date);
    }
    if (filters.end_date) {
      params.append('end_date', filters.end_date);
    }
    if (filters.limit !== undefined) {
      params.append('limit', filters.limit.toString());
    }
    
    const url = `/sms/scheduler/logs${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await api.get(url);
    return response.data;
  } catch (error: any) {
    const detail = error?.response?.data?.detail || 'Failed to get scheduler logs';
    throw new Error(detail);
  }
};

/**
 * Get current scheduler status and registered jobs
 * Returns scheduler status with active jobs information
 */
export const getSchedulerStatus = async (): Promise<SchedulerStatus> => {
  try {
    const response = await api.get('/sms/scheduler/status');
    return response.data;
  } catch (error: any) {
    const detail = error?.response?.data?.detail || 'Failed to get scheduler status';
    throw new Error(detail);
  }
};

/**
 * Manually trigger a scheduler run for testing
 * Triggers immediate execution of a scheduler configuration
 */
export const manuallyTriggerScheduler = async (configId: number): Promise<{message: string}> => {
  try {
    const response = await api.post(`/sms/scheduler/run/${configId}`);
    return response.data;
  } catch (error: any) {
    const detail = error?.response?.data?.detail || 'Failed to trigger scheduler';
    throw new Error(detail);
  }
};

// ============================================================================
// RENEWAL DASHBOARD API FUNCTIONS
// ============================================================================

import { 
  RenewalDashboardMetrics, 
  RenewalDashboardFilters,
  RenewalDashboardFilterOptions,
  ChartData,
  RenewalPoliciesResponse,
  PaginationParams,
  RenewalPerformanceStats
} from '@/types/renewalDashboard';

/**
 * Get comprehensive renewal dashboard metrics
 * Returns complete dashboard data including summary, expiry breakdown, SMS metrics, and scheduler metrics
 */
export const getRenewalDashboardMetrics = async (filters: RenewalDashboardFilterOptions): Promise<RenewalDashboardMetrics> => {
  try {
    const params = new URLSearchParams();
    
    if (filters.start_date) {
      params.append('start_date', filters.start_date);
    }
    if (filters.end_date) {
      params.append('end_date', filters.end_date);
    }
    if (filters.date_range) {
      params.append('date_range', filters.date_range);
    }
    if (filters.month) {
      params.append('month', filters.month);
    }
    if (filters.product_name) {
      // Handle single value (string) or array for backward compatibility
      if (Array.isArray(filters.product_name)) {
        filters.product_name.forEach(name => params.append('product_name', name));
      } else {
        params.append('product_name', filters.product_name);
      }
    }
    if (filters.lob) {
      // Handle single value (string) or array for backward compatibility
      if (Array.isArray(filters.lob)) {
        filters.lob.forEach(lob => params.append('lob', lob));
      } else {
        params.append('lob', filters.lob);
      }
    }
    if (filters.channel) {
      // Handle single value (string) or array for backward compatibility
      if (Array.isArray(filters.channel)) {
        filters.channel.forEach(ch => params.append('channel', ch));
      } else {
        params.append('channel', filters.channel);
      }
    }
    if (filters.sub_channel) {
      // Handle single value (string) or array for backward compatibility
      if (Array.isArray(filters.sub_channel)) {
        filters.sub_channel.forEach(sc => params.append('sub_channel', sc));
      } else {
        params.append('sub_channel', filters.sub_channel);
      }
    }
    
    const url = `/renewal-dashboard/metrics${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await api.get(url);
    return response.data;
  } catch (error: any) {
    const detail = error?.response?.data?.detail || 'Failed to get renewal dashboard metrics';
    throw new Error(detail);
  }
};

/**
 * Download SMS metrics transaction data as Excel file
 * Downloads XLSX file with SMS transaction records and link tracking data
 */
export const downloadSmsMetricsTransactions = async (filters: RenewalDashboardFilterOptions): Promise<void> => {
  try {
    const params = new URLSearchParams();
    
    if (filters.start_date) {
      params.append('start_date', filters.start_date);
    }
    if (filters.end_date) {
      params.append('end_date', filters.end_date);
    }
    if (filters.date_range) {
      params.append('date_range', filters.date_range);
    }
    if (filters.month) {
      params.append('month', filters.month);
    }
    if (filters.branch_office) {
      if (Array.isArray(filters.branch_office)) {
        filters.branch_office.forEach((bo: string) => params.append('branch_office', bo));
      } else {
        params.append('branch_office', filters.branch_office);
      }
    }
    if (filters.product_code) {
      if (Array.isArray(filters.product_code)) {
        filters.product_code.forEach((pc: string) => params.append('product_code', pc));
      } else {
        params.append('product_code', filters.product_code);
      }
    }
    if (filters.product_name) {
      if (Array.isArray(filters.product_name)) {
        filters.product_name.forEach((name: string) => params.append('product_name', name));
      } else {
        params.append('product_name', filters.product_name);
      }
    }
    if (filters.agent_category) {
      if (Array.isArray(filters.agent_category)) {
        filters.agent_category.forEach((ac: string) => params.append('agent_category', ac));
      } else {
        params.append('agent_category', filters.agent_category);
      }
    }
    if (filters.agent_code) {
      if (Array.isArray(filters.agent_code)) {
        filters.agent_code.forEach((ac: string) => params.append('agent_code', ac));
      } else {
        params.append('agent_code', filters.agent_code);
      }
    }
    if (filters.sms_status) {
      params.append('sms_status', filters.sms_status);
    }
    if (filters.lob) {
      if (Array.isArray(filters.lob)) {
        filters.lob.forEach(lob => params.append('lob', lob));
      } else {
        params.append('lob', filters.lob);
      }
    }
    
    const url = `/renewal-dashboard/metrics/sms/export${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await api.get(url, {
      responseType: 'blob'
    });
    
    // Create a download link
    const url_blob = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url_blob;
    
    // Extract filename from content-disposition header or use default
    const contentDisposition = response.headers['content-disposition'];
    let filename = `sms_metrics_transactions_${new Date().getTime()}.xlsx`;
    
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1];
      }
    }
    
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url_blob);
  } catch (error: any) {
    const detail = error?.response?.data?.detail || 'Failed to download SMS metrics transactions';
    throw new Error(detail);
  }
};

/**
 * Download renewal dashboard summary as Excel file
 * Downloads XLSX file with summary metrics and applied filters
 */
export const downloadRenewalDashboardSummary = async (filters: RenewalDashboardFilterOptions): Promise<void> => {
  try {
    const params = new URLSearchParams();
    
    if (filters.start_date) {
      params.append('start_date', filters.start_date);
    }
    if (filters.end_date) {
      params.append('end_date', filters.end_date);
    }
    if (filters.date_range) {
      params.append('date_range', filters.date_range);
    }
    if (filters.month) {
      params.append('month', filters.month);
    }
    if (filters.branch_office) {
      if (Array.isArray(filters.branch_office)) {
        filters.branch_office.forEach((bo: string) => params.append('branch_office', bo));
      } else {
        params.append('branch_office', filters.branch_office);
      }
    }
    if (filters.product_code) {
      if (Array.isArray(filters.product_code)) {
        filters.product_code.forEach((pc: string) => params.append('product_code', pc));
      } else {
        params.append('product_code', filters.product_code);
      }
    }
    if (filters.product_name) {
      if (Array.isArray(filters.product_name)) {
        filters.product_name.forEach((name: string) => params.append('product_name', name));
      } else {
        params.append('product_name', filters.product_name);
      }
    }
    if (filters.agent_category) {
      if (Array.isArray(filters.agent_category)) {
        filters.agent_category.forEach((ac: string) => params.append('agent_category', ac));
      } else {
        params.append('agent_category', filters.agent_category);
      }
    }
    if (filters.agent_code) {
      if (Array.isArray(filters.agent_code)) {
        filters.agent_code.forEach((ac: string) => params.append('agent_code', ac));
      } else {
        params.append('agent_code', filters.agent_code);
      }
    }
    if (filters.sms_status) {
      params.append('sms_status', filters.sms_status);
    }
    if (filters.lob) {
      if (Array.isArray(filters.lob)) {
        filters.lob.forEach(lob => params.append('lob', lob));
      } else {
        params.append('lob', filters.lob);
      }
    }
    
    const url = `/renewal-dashboard/metrics/summary/export${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await api.get(url, {
      responseType: 'blob'
    });
    
    // Create a download link
    const url_blob = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url_blob;
    
    // Extract filename from content-disposition header or use default
    const contentDisposition = response.headers['content-disposition'];
    let filename = `renewal_dashboard_summary_${new Date().getTime()}.xlsx`;
    
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1];
      }
    }
    
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url_blob);
  } catch (error: any) {
    const detail = error?.response?.data?.detail || 'Failed to download renewal dashboard summary';
    throw new Error(detail);
  }
};

/**
 * Get renewal dashboard filter options
 * Returns all available filter options for the dashboard
 */
export const getRenewalDashboardFilters = async (): Promise<RenewalDashboardFilters> => {
  try {
    const response = await api.get('/renewal-dashboard/filters');
    return response.data;
  } catch (error: any) {
    const detail = error?.response?.data?.detail || 'Failed to get renewal dashboard filters';
    throw new Error(detail);
  }
};

/**
 * Get dependent filter options based on selected channel, sub-channel, LOB, and/or product_name
 * Returns sub-channels filtered by channel, LOBs filtered by sub-channel, product names filtered by LOB, and/or channels filtered by product_name
 */
export const getRenewalDashboardDependentFilters = async (
  channel?: string | string[], 
  sub_channel?: string | string[], 
  lob?: string | string[],
  product_name?: string | string[]
): Promise<{ sub_channels: string[]; lobs: string[]; product_names: string[]; channels: string[] }> => {
  try {
    const params = new URLSearchParams();
    // Handle arrays - append multiple values for each filter
    if (channel) {
      if (Array.isArray(channel)) {
        channel.forEach(ch => params.append('channel', ch));
      } else {
        params.append('channel', channel);
      }
    }
    if (sub_channel) {
      if (Array.isArray(sub_channel)) {
        sub_channel.forEach(sc => params.append('sub_channel', sc));
      } else {
        params.append('sub_channel', sub_channel);
      }
    }
    if (lob) {
      if (Array.isArray(lob)) {
        lob.forEach(l => params.append('lob', l));
      } else {
        params.append('lob', lob);
      }
    }
    if (product_name) {
      if (Array.isArray(product_name)) {
        product_name.forEach(pn => params.append('product_name', pn));
      } else {
        params.append('product_name', product_name);
      }
    }
    const response = await api.get(`/renewal-dashboard/filters/dependent?${params.toString()}`);
    return {
      sub_channels: response.data.sub_channels || [],
      lobs: response.data.lobs || [],
      product_names: response.data.product_names || [],
      channels: response.data.channels || []
    };
  } catch (error: any) {
    const detail = error?.response?.data?.detail || 'Failed to get renewal dashboard dependent filter options';
    throw new Error(detail);
  }
};

/**
 * Get renewal dashboard chart data
 * Returns chart data for specific chart types
 */
export const getRenewalDashboardCharts = async (chartType: string, filters: RenewalDashboardFilterOptions): Promise<ChartData> => {
  try {
    const params = new URLSearchParams();
    
    if (filters.start_date) {
      params.append('start_date', filters.start_date);
    }
    if (filters.end_date) {
      params.append('end_date', filters.end_date);
    }
    if (filters.date_range) {
      params.append('date_range', filters.date_range);
    }
    if (filters.month) {
      params.append('month', filters.month);
    }
    if (filters.product_name) {
      // Handle single value (string) or array for backward compatibility
      if (Array.isArray(filters.product_name)) {
        filters.product_name.forEach(name => params.append('product_name', name));
      } else {
        params.append('product_name', filters.product_name);
      }
    }
    if (filters.lob) {
      // Handle single value (string) or array for backward compatibility
      if (Array.isArray(filters.lob)) {
        filters.lob.forEach(lob => params.append('lob', lob));
      } else {
        params.append('lob', filters.lob);
      }
    }
    if (filters.channel) {
      // Handle single value (string) or array for backward compatibility
      if (Array.isArray(filters.channel)) {
        filters.channel.forEach(ch => params.append('channel', ch));
      } else {
        params.append('channel', filters.channel);
      }
    }
    if (filters.sub_channel) {
      // Handle single value (string) or array for backward compatibility
      if (Array.isArray(filters.sub_channel)) {
        filters.sub_channel.forEach(sc => params.append('sub_channel', sc));
      } else {
        params.append('sub_channel', filters.sub_channel);
      }
    }
    
    const url = `/renewal-dashboard/charts/${chartType}${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await api.get(url);
    return response.data;
  } catch (error: any) {
    const detail = error?.response?.data?.detail || 'Failed to get renewal dashboard chart data';
    throw new Error(detail);
  }
};

/**
 * Calculate RED date from date_range (T+X or T-X format)
 * Returns the calculated date as YYYY-MM-DD string, or null if invalid
 */
const calculateRedDateFromRange = (dateRange: string): string | null => {
  if (!dateRange || dateRange === 'custom') return null;
  
  const match = dateRange.match(/^T([+-])(\d+)$/);
  if (!match) return null;
  
  const operator = match[1];
  const days = parseInt(match[2], 10);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  let targetDate: Date;
  if (operator === '-') {
    // T-X means RED = X days FROM today (future) - matches database days_offset = +X
    targetDate = new Date(today);
    targetDate.setDate(today.getDate() + days);
  } else {
    // T+X means RED = X days BEFORE today (past) - matches database days_offset = -X
    targetDate = new Date(today);
    targetDate.setDate(today.getDate() - days);
  }
  
  return targetDate.toISOString().split('T')[0];
};

/**
 * Get paginated renewal policies
 * Returns filtered and paginated list of renewal policies
 */
export const getRenewalPolicies = async (filters: RenewalDashboardFilterOptions, pagination: PaginationParams): Promise<RenewalPoliciesResponse> => {
  try {
    const params = new URLSearchParams();
    
    // Add pagination parameters
    params.append('page', pagination.page.toString());
    params.append('page_size', pagination.page_size.toString());
    if (pagination.sort_by) {
      params.append('sort_by', pagination.sort_by);
    }
    if (pagination.sort_order) {
      params.append('sort_order', pagination.sort_order);
    }
    
    // Handle date filtering: convert date_range or red_from/red_to to start_date/end_date
    // Priority: start_date/end_date (explicit) > red_from/red_to > date_range calculation
    // If date_range is set (T+X or T-X), calculate RED date and use it for both start and end (exact match)
    // If red_from/red_to are set (manual RED date selection), use them as start_date/end_date
    // Don't send date_range parameter - send calculated dates instead
    if (filters.start_date && filters.end_date) {
      // If start_date and end_date are explicitly set, use them
      params.append('start_date', filters.start_date);
      params.append('end_date', filters.end_date);
    } else if (filters.red_from || filters.red_to) {
      // If RED date is manually selected, use red_from/red_to as start_date/end_date
      // They should be the same value (exact match), but handle both for safety
      const redDate = filters.red_from || filters.red_to;
      if (redDate) {
        params.append('start_date', redDate);
        params.append('end_date', redDate);
      }
    } else if (filters.date_range && filters.date_range !== 'custom') {
      // If date_range is set, calculate RED date and use it for both start and end (exact match)
      const calculatedDate = calculateRedDateFromRange(filters.date_range);
      if (calculatedDate) {
        params.append('start_date', calculatedDate);
        params.append('end_date', calculatedDate);
      }
      // Don't send date_range parameter - we're sending the calculated dates instead
    } else if (filters.start_date) {
      params.append('start_date', filters.start_date);
    } else if (filters.end_date) {
      params.append('end_date', filters.end_date);
    }
    if (filters.month) {
      params.append('month', filters.month);
    }
    if (filters.product_name) {
      // Handle single value (string) or array for backward compatibility
      if (Array.isArray(filters.product_name)) {
        filters.product_name.forEach(name => params.append('product_name', name));
      } else {
        params.append('product_name', filters.product_name);
      }
    }
    if (filters.policy_number) {
      params.append('policy_number', filters.policy_number);
    }
    if (filters.lob) {
      // Handle single value (string) or array for backward compatibility
      if (Array.isArray(filters.lob)) {
        filters.lob.forEach(lob => params.append('lob', lob));
      } else {
        params.append('lob', filters.lob);
      }
    }
    if (filters.channel) {
      // Handle single value (string) or array for backward compatibility
      if (Array.isArray(filters.channel)) {
        filters.channel.forEach(ch => params.append('channel', ch));
      } else {
        params.append('channel', filters.channel);
      }
    }
    if (filters.sub_channel) {
      // Handle single value (string) or array for backward compatibility
      if (Array.isArray(filters.sub_channel)) {
        filters.sub_channel.forEach(sc => params.append('sub_channel', sc));
      } else {
        params.append('sub_channel', filters.sub_channel);
      }
    }
    
    const url = `/renewal-dashboard/policies${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await api.get(url);
    return response.data;
  } catch (error: any) {
    const detail = error?.response?.data?.detail || 'Failed to get renewal policies';
    throw new Error(detail);
  }
};

/**
 * Get renewal dashboard performance statistics
 * Returns performance metrics and system health information
 */
export const getRenewalPerformanceStats = async (): Promise<RenewalPerformanceStats> => {
  try {
    const response = await api.get('/renewal-dashboard/performance');
    return response.data;
  } catch (error: any) {
    const detail = error?.response?.data?.detail || 'Failed to get renewal performance stats';
    throw new Error(detail);
  }
};

/**
 * Refresh renewal dashboard data
 * Triggers refresh of materialized views and cached data
 */
export const refreshRenewalDashboard = async (): Promise<{message: string}> => {
  try {
    const response = await api.post('/renewal-dashboard/refresh');
    return response.data;
  } catch (error: any) {
    const detail = error?.response?.data?.detail || 'Failed to refresh renewal dashboard';
    throw new Error(detail);
  }
};
